import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Grid, File } from 'react-feather';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../../../../../config';

import ReportShell from '../../shared/ReportShell';
import ReportFilterBar from '../../shared/ReportFilterBar';
import ReportTable, { formatMoney } from '../../shared/ReportTable';
import ReportPagination from '../../shared/ReportPagination';
import ColumnPicker from '../../shared/ColumnPicker';
import useColumnConfig from '../../shared/useColumnConfig';
import useReportLookups from '../../shared/useReportLookups';
import useReportAccess from '../../shared/useReportAccess';

const PAGE_SIZE = 50;
const TODAY = new Date().toISOString().split('T')[0];

const yesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
};

const HOUR_COLS = Array.from({ length: 24 }, (_, h) => ({ key: `h${h}`, label: `${h}-${h + 1}hr` }));

// Pivot per-hour API rows into one row per tenant with h0..h23 net buckets.
const pivotHourly = (rows = []) => {
    const map = new Map();
    rows.forEach((r) => {
        const key = [r.tenant_code, r.property_name, r.category_name, r.subcategory_name, r.tenant_name].join('||');
        if (!map.has(key)) {
            const base = {
                tenant_name: r.tenant_name || '',
                tenant_code: r.tenant_code || '',
                property_name: r.property_name || '',
                category_name: r.category_name || '',
                subcategory_name: r.subcategory_name || '',
                total_net_sales: 0,
            };
            for (let h = 0; h < 24; h += 1) base[`h${h}`] = 0;
            map.set(key, base);
        }
        const row = map.get(key);
        const h = Number(r.hour);
        const net = Number(r.total_net_sales || 0);
        if (!Number.isNaN(h) && h >= 0 && h < 24) row[`h${h}`] += net;
        row.total_net_sales += net;
    });
    return Array.from(map.values());
};

const FIXED_COLUMNS = [
    { key: 'tenant_name', label: 'Tenant Name' },
    { key: 'tenant_code', label: 'Tcode' },
    { key: 'property_name', label: 'Property' },
    { key: 'category_name', label: 'Category' },
    { key: 'subcategory_name', label: 'Subcategory' },
];
const DEFAULT_COLUMNS = FIXED_COLUMNS.map((c) => c.key);

const initialFilters = {
    startDate: yesterday(),
    endDate: yesterday(),
    property_id: '',
    category_id: '',
    subcategory_id: '',
    group_name: '',
    selectedTenants: [],
};

const HourlySalesReportView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const {
        selectedColumns, onChange: onColumnsChange, columnOptions, visibleColumns: visibleFixed,
    } = useColumnConfig(FIXED_COLUMNS, DEFAULT_COLUMNS);
    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        categoryId: filters.category_id,
        subcategoryId: filters.subcategory_id,
        groupName: filters.group_name,
    });
    const { concessionaireId, propertyAccessNames } = lookups;

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'category_id') { next.subcategory_id = ''; next.selectedTenants = []; }
            if (key === 'property_id') { next.selectedTenants = []; }
            if (key === 'subcategory_id') { next.selectedTenants = []; }
            if (key === 'group_name') { next.selectedTenants = []; }
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const generateReport = async () => {
        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            property_access: propertyAccessNames,
            start_date: filters.startDate,
            end_date: filters.endDate,
            group_name: filters.group_name,
            category_name: nameOf(lookups.categoryOptions, filters.category_id),
            property_name: nameOf(lookups.propertyOptions, filters.property_id),
            subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
            tenant_id: filters.selectedTenants,
            floor: '',
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/gethourlysalesreport`, payload, { headers });
            setReportData(pivotHourly(Array.isArray(res.data) ? res.data : []));
            setHasRun(true);
            insertToLog(' generated Hourly Sales report');
        } catch (err) {
            console.error('hourly sales report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Hourly Sales report'));
        } finally {
            setLoading(false);
        }
    };

    const sortedData = useMemo(() => {
        const data = [...reportData];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    }, [reportData, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    const columns = useMemo(() => {
        const hourCols = HOUR_COLS.map((c, h) => ({ key: c.key, label: t('{{from}}-{{to}}hr', { from: h, to: h + 1 }), isMoney: true, align: 'right', sortable: false }));
        const totalCol = { key: 'total_net_sales', label: t('Total Net'), isMoney: true, align: 'right', sortable: false };
        return [...visibleFixed.map((c) => ({ ...c, label: t(c.label) })), ...hourCols, totalCol];
    }, [visibleFixed, t]);

    const totals = useMemo(() => {
        if (!reportData.length) return null;
        const t = { total_net_sales: 0 };
        HOUR_COLS.forEach((c) => { t[c.key] = 0; });
        reportData.forEach((r) => {
            HOUR_COLS.forEach((c) => { t[c.key] += Number(r[c.key] || 0); });
            t.total_net_sales += Number(r.total_net_sales || 0);
        });
        return t;
    }, [reportData]);

    const fields = [
        { key: 'startDate', label: t('Start date'), type: 'date', role: 'startDate', max: TODAY },
        { key: 'endDate', label: t('End date'), type: 'date', role: 'endDate', max: TODAY },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'category_id', label: t('Category'), type: 'select', options: lookups.categoryOptions, placeholder: t('All categories') },
        { key: 'subcategory_id', label: t('Sub category'), type: 'select', options: lookups.subcategoryOptions, placeholder: t('All sub categories') },
        { key: 'group_name', label: t('Group'), type: 'select', options: lookups.groupOptions, placeholder: t('All groups') },
        { key: 'selectedTenants', label: t('Tenants'), type: 'multiselect', options: lookups.tenantOptions, placeholder: t('All tenants'), span: 2 },
    ];

    const exportMeta = () => ([
        filters.startDate === filters.endDate ? [t('Date'), filters.startDate] : [t('Date Range'), t('{{start}} to {{end}}', { start: filters.startDate, end: filters.endDate })],
        [t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')],
        [t('Category'), nameOf(lookups.categoryOptions, filters.category_id) || t('All')],
        [t('Subcategory'), nameOf(lookups.subcategoryOptions, filters.subcategory_id) || t('All')],
        [t('Group'), filters.group_name || t('All')],
        [t('Tenants'), filters.selectedTenants.length ? t('{{count}} selected', { count: filters.selectedTenants.length }) : t('All')],
        [t('Exported By'), login_name],
        [t('Exported On'), new Date().toLocaleString()],
    ]);

    const exportToExcel = () => {
        if (!sortedData.length) return;
        const headerRow = [t('Tenant Name'), t('Tcode'), t('Property'), t('Category'), t('Subcategory'), ...HOUR_COLS.map((c, h) => t('{{from}}-{{to}}hr', { from: h, to: h + 1 })), t('Total Net')];
        const colCount = headerRow.length;
        const pad = (arr) => { const r = Array(colCount).fill(''); arr.forEach((v, i) => { if (i < colCount) r[i] = v; }); return r; };
        const sheetData = [pad([t('Hourly Sales Report')]), pad([''])];
        exportMeta().forEach((l) => sheetData.push(pad(l)));
        sheetData.push(pad(['']));
        const headerRowIndex = sheetData.length;
        sheetData.push(headerRow);
        sortedData.forEach((r) => sheetData.push([
            r.tenant_name || '', r.tenant_code || '', r.property_name || '', r.category_name || '', r.subcategory_name || '',
            ...HOUR_COLS.map((c) => Number(r[c.key] || 0)), Number(r.total_net_sales || 0),
        ]));
        const totalRow = [t('Total'), '', '', '', '', ...HOUR_COLS.map((c) => Number(totals?.[c.key] || 0)), Number(totals?.total_net_sales || 0)];
        sheetData.push(totalRow);

        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }];
        ws['!cols'] = [{ wch: 28 }, { wch: 12 }, { wch: 20 }, { wch: 18 }, { wch: 20 }, ...Array.from({ length: 24 }, () => ({ wch: 10 })), { wch: 14 }];
        ws['!freeze'] = { xSplit: 5, ySplit: headerRowIndex + 1 };
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R = headerRowIndex + 1; R <= range.e.r; R += 1) {
            for (let C = 5; C <= range.e.c; C += 1) {
                const addr = XLSX.utils.encode_cell({ r: R, c: C });
                if (ws[addr]) ws[addr].z = '#,##0.00';
            }
        }
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Hourly Sales');
        XLSX.writeFile(wb, `Hourly_Sales_Report_${filters.startDate}_to_${filters.endDate}.xlsx`);
        insertToLog(' exported Hourly Sales report to excel');
    };

    const exportToPDF = () => {
        if (!sortedData.length) return;
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(13);
        doc.text(t('Hourly Sales Report'), 14, 14);
        doc.setFontSize(8);
        let y = 22;
        exportMeta().forEach(([k, v]) => { doc.text(`${k}: ${v}`, 14, y); y += 5; });
        const head = [t('Tenant'), t('Tcode'), t('Property'), t('Category'), t('Subcat'), ...HOUR_COLS.map((c, h) => t('{{from}}-{{to}}hr', { from: h, to: h + 1 })), t('Total')];
        const body = sortedData.map((r) => [
            r.tenant_name, r.tenant_code, r.property_name, r.category_name, r.subcategory_name,
            ...HOUR_COLS.map((c) => formatMoney(r[c.key])), formatMoney(r.total_net_sales),
        ]);
        const foot = [[t('Total'), '', '', '', '', ...HOUR_COLS.map((c) => formatMoney(totals?.[c.key])), formatMoney(totals?.total_net_sales)]];
        doc.autoTable({
            head: [head], body, foot, startY: y + 3,
            styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
            headStyles: { fillColor: [0, 71, 178], textColor: 255, fontSize: 7, halign: 'center' },
            footStyles: { fillColor: [230, 235, 245], textColor: 0, fontStyle: 'bold' },
            theme: 'grid',
            // 29 cols won't fit one page — paginate columns, repeating Tenant.
            horizontalPageBreak: true,
            horizontalPageBreakRepeat: 0,
            columnStyles: { 0: { cellWidth: 30 } },
        });
        doc.save(`Hourly_Sales_Report_${filters.startDate}_to_${filters.endDate}.pdf`);
        insertToLog(' exported Hourly Sales report to pdf');
    };

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('Hourly Sales Report')}
                subtitle={t('Net sales by hour of day (0–24h) per tenant over the selected dates.')}
                actions={(
                    <Fragment>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToExcel} disabled={!sortedData.length} title={t('Export to Excel')}>
                            <Grid size={14} /> {t('Excel')}
                        </button>
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={exportToPDF} disabled={!sortedData.length} title={t('Export to PDF')}>
                            <File size={14} /> {t('PDF')}
                        </button>
                    </Fragment>
                )}
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <ColumnPicker
                    options={columnOptions.map((o) => ({ ...o, label: t(o.label) }))}
                    value={selectedColumns}
                    total={FIXED_COLUMNS.length}
                    onChange={onColumnsChange}
                />

                <ReportTable
                    columns={columns}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    totals={totals}
                    loading={loading}
                    error={error}
                    emptyMessage={hasRun
                        ? t('No data found for the selected filters. Try widening the date range or clearing filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('tenants')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default HourlySalesReportView;
